import { useState } from 'react';
import {
  useCreateGoalMutation,
  useCopyGoalMutation,
  useUpdateGoalMutation,
  useDeleteGoalMutation,
} from './useGoalMutation';

export const useGoalsLogic = setRecentlyMovedGoalId => {
  const { createGoalMutation } = useCreateGoalMutation();
  const { copyGoalMutation } = useCopyGoalMutation();
  const { updateGoalMutation } = useUpdateGoalMutation();
  const { deleteGoalMutation } = useDeleteGoalMutation();

  const [isAddingGoal, setIsAddingGoal] = useState(false);
  const [newGoal, setNewGoal] = useState({
    goal: '',
    planYear: new Date().getFullYear(),
  });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [originalGoalData, setOriginalGoalData] = useState({});

  // New state for unsaved changes modal
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const hasUnsavedChanges = () => {
    // Check if adding a goal and has entered text
    if (isAddingGoal && newGoal.goal.trim()) {
      return true;
    }

    // Check if editing and data has changed
    if (editingId && editData.goal !== undefined) {
      const hasChanges =
        editData.goal !== originalGoalData.goal ||
        editData.planYear !== originalGoalData.planYear;
      return hasChanges;
    }

    return false;
  };

  // Enhanced cancel functions that check for unsaved changes
  const handleCancelWithUnsavedCheck = actionCallback => {
    if (hasUnsavedChanges()) {
      setPendingAction(() => actionCallback);
      setShowUnsavedChangesModal(true);
    } else {
      actionCallback();
    }
  };

  // Modal action handlers
  const handleSaveAndContinue = () => {
    if (isAddingGoal) {
      addGoal();
    } else if (editingId) {
      saveEdit();
    }

    setShowUnsavedChangesModal(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const handleDiscardChanges = () => {
    setShowUnsavedChangesModal(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const handleCloseModal = () => {
    setShowUnsavedChangesModal(false);
    setPendingAction(null);
  };

  const addGoal = () => {
    if (!newGoal.goal.trim()) return;

    const goalData = {
      name: newGoal.goal,
      plan_year: newGoal.planYear,
    };

    createGoalMutation(goalData);

    setNewGoal({
      goal: '',
      planYear: new Date().getFullYear(),
    });
    setIsAddingGoal(false);
  };

  const copyGoal = id => {
    copyGoalMutation(id);
  };

  const deleteGoal = goal => {
    deleteGoalMutation(goal);
    if (editingId === goal.id) {
      setEditingId(null);
      setEditData({});
      setOriginalGoalData({});
    }
  };

  const startEditing = goal => {
    setEditingId(goal.id);
    setEditData(goal);
    // Store original data to compare against later
    setOriginalGoalData(goal);
  };

  const saveEdit = () => {
    if (editData.goal.trim()) {
      const updateData = {
        id: editingId,
        name: editData.goal,
        plan_year: editData.planYear,
      };

      // Check if the year was changed
      const yearChanged = originalGoalData.planYear !== editData.planYear;

      updateGoalMutation(updateData);

      // If year changed, mark this goal as recently moved after a delay
      if (yearChanged && setRecentlyMovedGoalId) {
        setTimeout(() => {
          setRecentlyMovedGoalId(editingId);
          // Clear the highlight after 3 seconds
          setTimeout(() => {
            setRecentlyMovedGoalId(null);
          }, 3000);
        }, 300);
      }

      setEditingId(null);
      setEditData({});
      setOriginalGoalData({});
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
    setOriginalGoalData({});
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      addGoal();
    } else if (e.key === 'Escape') {
      if (isAddingGoal) {
        handleCancelAdd();
      } else if (editingId) {
        handleCancelEdit();
      }
    }
  };

  // Enhanced cancel functions
  const handleCancelAdd = () => {
    setIsAddingGoal(false);
    setNewGoal({
      goal: '',
      planYear: new Date().getFullYear(),
    });
  };

  const handleCancelEdit = () => {
    cancelEdit();
  };

  return {
    // State
    isAddingGoal,
    setIsAddingGoal,
    newGoal,
    setNewGoal,
    editingId,
    editData,
    setEditData,
    // Unsaved changes modal state
    showUnsavedChangesModal,
    setShowUnsavedChangesModal, // Export for navigation blocker
    hasUnsavedChanges: hasUnsavedChanges(),
    setPendingAction, // Export for navigation blocker
    // Actions
    addGoal,
    copyGoal,
    deleteGoal,
    startEditing,
    saveEdit,
    cancelEdit,
    handleKeyDown,
    handleCancelAdd,
    // Enhanced actions with unsaved changes detection
    handleCancelEdit,
    handleCancelWithUnsavedCheck,
    // Modal actions
    handleSaveAndContinue,
    handleDiscardChanges,
    handleCloseModal,
  };
};
